import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

/* ── Responsive column count via JS (same pattern as SalonCarousel) ── */
const useWindowWidth = () => {
  const [width, setWidth] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 1200));
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler, { passive: true });
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
};

const VisionCarousel = ({ initialData }) => {
  const { t } = useTranslation('home');
  const [visions, setVisions] = useState(() => initialData ?? []);
  const [loading, setLoading] = useState(!initialData);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [enableTransition, setEnableTransition] = useState(true);
  const autoplayRef = useRef(null);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const navigate = useNavigate();
  const windowWidth = useWindowWidth();

  const cols = windowWidth <= 480 ? 2 : windowWidth <= 768 ? 3 : windowWidth <= 1024 ? 4 : 6;
  const gapPx = 12;
  const total = visions.length;
  // Triple the array so there's always a copy before and after the visible window
  const tripled = total > 0 ? [...visions, ...visions, ...visions] : [];
  // Pixel-exact item sizing — eliminates gap-accounting errors in %-based transforms
  const itemWidthPx = containerWidth > 0 ? (containerWidth - gapPx * (cols - 1)) / cols : 0;
  const stepPx = itemWidthPx + gapPx;

  // Measure the overflow container in pixels — re-runs when data arrives (total 0→N)
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const ro = new ResizeObserver(() => setContainerWidth(node.offsetWidth));
    ro.observe(node);
    setContainerWidth(node.offsetWidth);
    return () => ro.disconnect();
  }, [total]);

  // Start at the middle copy so prev and next are both infinite from the first frame
  useEffect(() => {
    if (total > 0) { setCurrentIndex(total); setEnableTransition(false); }
  }, [total]);

  // Re-enable CSS transition one double-RAF after any silent jump
  useEffect(() => {
    if (!enableTransition) {
      const raf = requestAnimationFrame(() =>
        requestAnimationFrame(() => setEnableTransition(true))
      );
      return () => cancelAnimationFrame(raf);
    }
  }, [enableTransition]);

  // Seamless boundary wrap — fires AFTER the 600ms CSS transition completes
  useEffect(() => {
    if (total === 0) return;
    let tid;
    if (currentIndex >= total * 2) {
      // Stepped into last copy → silently jump to same position in middle copy
      tid = setTimeout(() => { setEnableTransition(false); setCurrentIndex(p => p - total); }, 620);
    } else if (currentIndex < total) {
      // Stepped into first copy → silently jump to same position in middle copy
      tid = setTimeout(() => { setEnableTransition(false); setCurrentIndex(p => p + total); }, 620);
    }
    return () => clearTimeout(tid);
  }, [currentIndex, total]);

  // Reset to middle copy on responsive breakpoint change to avoid stale offsets
  useEffect(() => {
    if (total > 0) { setCurrentIndex(total); setEnableTransition(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- total is read as a snapshot; total changes are already handled by the effect above ([total])
  }, [cols]);

  const stopAutoplay = useCallback(() => {
    if (autoplayRef.current) clearInterval(autoplayRef.current);
  }, []);

  const startAutoplay = useCallback(() => {
    stopAutoplay();
    autoplayRef.current = setInterval(() => setCurrentIndex(p => p + 1), 2500);
  }, [stopAutoplay]);

  useEffect(() => {
    if (total > 0 && containerWidth > 0) startAutoplay();
    return stopAutoplay;
  }, [total, containerWidth, startAutoplay, stopAutoplay]);

  const prev = () => { stopAutoplay(); setCurrentIndex(p => p - 1); startAutoplay(); };
  const next = () => { stopAutoplay(); setCurrentIndex(p => p + 1); startAutoplay(); };

  useEffect(() => {
    if (initialData) return; // already seeded from /api/home
    (async () => {
      try {
        const url = `${import.meta.env.VITE_REACT_APP_API_URL}/api/portfolio-public/gallery?limit=12&sort=popular`;
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (data.success && data.data.length > 0) setVisions(data.data);
      } catch (err) {
        console.error("Failed to fetch portfolio images:", err);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only fallback fetch; initialData is checked once, not a re-trigger condition
  }, []);

  if (loading) {
    return (
      <section style={{ padding: "48px 0", background: "#ffffff" }}>
        <div style={{ textAlign: "center", color: "#a346d5", fontSize: "13px", fontWeight: 500 }}>
          {t('vision_loading')}
        </div>
      </section>
    );
  }

  if (total === 0) return null;

  const showNavBtns = windowWidth > 480;

  const navBtnBase = {
    position: "absolute", top: "50%", transform: "translateY(-50%)",
    zIndex: 10, cursor: "pointer", width: 38, height: 38, borderRadius: 12,
    background: "#ffffff", border: "1px solid #ebebef",
    boxShadow: "0 1px 3px rgba(13,22,25,0.04)", display: showNavBtns ? "flex" : "none",
    alignItems: "center", justifyContent: "center", transition: "all 0.2s cubic-bezier(0.22, 1, 0.36, 1)",
  };

  return (
    <section style={{ padding: "64px 0", background: "#ffffff" }}>

      {/* Header */}
      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "0 32px 36px" }}>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <p style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: "#a346d5", marginBottom: "8px" }}>
              {t('vision_eyebrow')}
            </p>
            <h2 style={{ fontSize: "clamp(20px, 3vw, 28px)", fontWeight: 800, letterSpacing: "-0.02em", color: "#0f0f14", lineHeight: 1.2, margin: 0 }}>
              {t('vision_heading')}
            </h2>
            <p style={{ fontSize: "13px", color: "#9a9aab", marginTop: "6px" }}>
              {t('vision_subheading')}
            </p>
          </div>

          <button
            onClick={() => navigate("/gallery")}
            style={{
              display: "inline-flex", alignItems: "center", gap: "8px",
              padding: "10px 22px", borderRadius: "99px",
              background: "linear-gradient(90deg, #ef6997 0%, #e85cba 50%, #b84cdc 100%)",
              border: "none", color: "#fff", cursor: "pointer",
              fontSize: "13px", fontWeight: 600, flexShrink: 0,
              boxShadow: "0 4px 16px rgba(184,76,220,0.35)",
              transition: "transform 0.2s, box-shadow 0.2s",
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.05)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(184,76,220,0.52)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(184,76,220,0.35)"; }}
          >
            {t('vision_cta')} <ArrowRight style={{ width: 15, height: 15 }} />
          </button>
        </div>
      </div>

      {/* Carousel */}
      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "0 12px", position: "relative" }}>

        <button
          style={{ ...navBtnBase, left: -19 }}
          onClick={prev}
          aria-label="Previous"
          onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-50%) scale(1.08)"; e.currentTarget.style.boxShadow = "0 4px 8px rgba(13,22,25,0.06), 0 16px 40px rgba(13,22,25,0.1)"; }}
          onMouseLeave={e => { e.currentTarget.style.transform = "translateY(-50%)"; e.currentTarget.style.boxShadow = "0 1px 3px rgba(13,22,25,0.04)"; }}
        >
          <ChevronLeft style={{ width: 16, height: 16, color: "#0f0f14" }} />
        </button>

        <div ref={containerRef} style={{ overflow: "hidden" }}>
          <div
            style={{
              display: "flex",
              gap: `${gapPx}px`,
              transform: `translateX(${-(currentIndex * stepPx)}px)`,
              transition: enableTransition ? "transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)" : "none",
              willChange: "transform",
            }}
          >
            {tripled.map((vision, idx) => (
              <div key={`${vision.id}-${idx}`} style={{ flexShrink: 0, width: `${itemWidthPx}px` }}>
                <div
                  style={{
                    cursor: "pointer", borderRadius: 16, overflow: "hidden",
                    position: "relative",
                    border: "1px solid #ebebef",
                    boxShadow: "0 1px 3px rgba(13,22,25,0.04), 0 12px 32px rgba(13,22,25,0.06)",
                    background: "#ffffff",
                    transition: "box-shadow 0.25s cubic-bezier(0.22, 1, 0.36, 1), transform 0.25s cubic-bezier(0.22, 1, 0.36, 1)",
                  }}
                  onClick={() => navigate(`/gallery/${vision.id}`)}
                  onMouseEnter={e => {
                    e.currentTarget.style.boxShadow = "0 4px 8px rgba(13,22,25,0.06), 0 16px 40px rgba(13,22,25,0.1)";
                    e.currentTarget.style.transform = "translateY(-4px)";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.boxShadow = "0 1px 3px rgba(13,22,25,0.04), 0 12px 32px rgba(13,22,25,0.06)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <img
                    src={vision.photo_url}
                    alt={vision.procedure_title || "Vision"}
                    style={{ width: "100%", height: 240, objectFit: "cover", display: "block" }}
                    draggable={false}
                    loading={idx < cols ? "eager" : "lazy"}
                  />
                  <div
                    style={{
                      position: "absolute", inset: 0, opacity: 0,
                      background: "linear-gradient(to top, rgba(15,15,20,0.65) 0%, transparent 55%)",
                      transition: "opacity 0.25s ease",
                      display: "flex", alignItems: "flex-end", padding: 12,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.opacity = "1"; }}
                    onMouseLeave={e => { e.currentTarget.style.opacity = "0"; }}
                  >
                    {vision.procedure_title && (
                      <p style={{ color: "#fff", fontSize: "11px", fontWeight: 600, margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {vision.procedure_title}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          style={{ ...navBtnBase, right: -19 }}
          onClick={next}
          aria-label="Next"
          onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-50%) scale(1.08)"; e.currentTarget.style.boxShadow = "0 4px 8px rgba(13,22,25,0.06), 0 16px 40px rgba(13,22,25,0.1)"; }}
          onMouseLeave={e => { e.currentTarget.style.transform = "translateY(-50%)"; e.currentTarget.style.boxShadow = "0 1px 3px rgba(13,22,25,0.04)"; }}
        >
          <ChevronRight style={{ width: 16, height: 16, color: "#0f0f14" }} />
        </button>
      </div>

    </section>
  );
};

export default VisionCarousel;